import { User, KernelListParams, KernelListResponse } from '@/types';
import type { Kernel } from '@/types/kernel';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api';

// ============================================================================
// API Configuration (injected at runtime from Server Component)
// ============================================================================

/**
 * Default kernel API URL fallback
 */
const DEFAULT_KERNEL_API_URL = 'http://localhost:8081';

/**
 * Kernel API base URL, set at runtime via setApiConfig
 */
let kernelApiBaseUrl: string | undefined;

/**
 * Set the API configuration. Called by AuthProvider on mount.
 */
export function setApiConfig(config: { kernelApiUrl: string }) {
  if (config.kernelApiUrl) {
    kernelApiBaseUrl = config.kernelApiUrl;
    console.log('[API] Kernel API URL configured:', kernelApiBaseUrl);
  }
}

/**
 * Get the current kernel API base URL
 */
export function getKernelApiBaseUrl(): string {
  return kernelApiBaseUrl || DEFAULT_KERNEL_API_URL;
}

// ============================================================================
// Token Provider for Authentication
// ============================================================================

/**
 * Function to get current access token, injected by AuthProvider
 */
let getAccessTokenFn: (() => Promise<string | null>) | null = null;

/**
 * Function to force refresh the token (bypasses expiry check), injected by AuthProvider
 */
let forceRefreshTokenFn: (() => Promise<string | null>) | null = null;

/**
 * Shared promise for in-flight token refresh to prevent concurrent refresh requests
 */
let refreshPromise: Promise<string | null> | null = null;

/**
 * Set the token provider functions. Called by AuthProvider on mount.
 */
export function setTokenProvider(
  getToken: () => Promise<string | null>,
  forceRefresh?: () => Promise<string | null>
) {
  getAccessTokenFn = getToken;
  forceRefreshTokenFn = forceRefresh ?? null;
}

/**
 * Force refresh token, deduplicating concurrent requests
 */
async function forceRefreshToken(): Promise<string | null> {
  if (!forceRefreshTokenFn) return null;
  
  if (refreshPromise) {
    return refreshPromise;
  }
  
  refreshPromise = forceRefreshTokenFn().finally(() => {
    refreshPromise = null;
  });
  
  return refreshPromise;
}

/**
 * Authenticated fetch wrapper that injects Authorization header
 * and handles 401 responses with automatic retry after token refresh.
 */
export async function authenticatedFetch(
  url: string, 
  options: RequestInit = {}
): Promise<Response> {
  const headers = new Headers(options.headers);
  
  // Get access token and add to headers
  const token = await getAccessTokenFn?.();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(url, { ...options, headers });

  // Handle 401 - force token refresh and retry once
  if (response.status === 401 && forceRefreshTokenFn) {
    const newToken = await forceRefreshToken();
    
    if (newToken) {
      const retryHeaders = new Headers(options.headers);
      retryHeaders.set('Authorization', `Bearer ${newToken}`);
      return fetch(url, { ...options, headers: retryHeaders });
    }
  }

  return response;
}

// ============================================================================
// User API Functions (internal API - may not need auth)
// ============================================================================

export async function fetchUsers(): Promise<User[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/users`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch users:', error);
    throw error;
  }
}

export async function createUser(userData: Omit<User, 'id'>): Promise<User> {
  try {
    const response = await fetch(`${API_BASE_URL}/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Failed to create user:', error);
    throw error;
  }
}

export async function updateUser(id: string, userData: Partial<User>): Promise<User> {
  try {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Failed to update user:', error);
    throw error;
  }
}

export async function deleteUser(id: string): Promise<void> {
  try {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error) {
    console.error('Failed to delete user:', error);
    throw error;
  }
}

// ============================================================================
// Kernel API Functions (mesh-vault - requires authentication)
// ============================================================================

export async function fetchKernels(params: KernelListParams = {}): Promise<KernelListResponse> {
  try {
    const page = params.page || 1;
    const pageSize = params.limit || 10;
    const filters = params.filters || [];
    
    console.log('Fetching kernels with parameters:', { page, page_size: pageSize, filters });
    
    // Convert filters to the expected API format
    const apiFilters = filters.map(filter => ({
      [filter.field]: filter.value
    }));
    
    const response = await authenticatedFetch(`${getKernelApiBaseUrl()}/api/v1/kernels/list`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page,
        page_size: pageSize,
        filter: apiFilters
      }),
    });
    
    console.log('Response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    const data = await response.json();
    console.log('Kernel API response:', data);
    return data;
  } catch (error) {
    console.error('Failed to fetch kernels:', error);
    throw error;
  }
}

export async function getTotalKernelCount(): Promise<number> {
  try {
    console.log('Starting to get total kernel count...');
    
    // Use the simple fetch function which now uses the correct API
    const response = await fetchKernelsSimple();
    console.log('Kernel list response:', response);
    
    // Return the total_count from the API response
    return response.total_count || 0;
  } catch (error) {
    console.error('Failed to get total kernel count:', error);
    // Return 0 if there's an error
    return 0;
  }
}

interface Mesh {
  id: string;
  name: string;
  version: string;
  status: string;
  is_root: boolean;
  created_at: string;
  updated_at: string;
}

export async function fetchMeshes(): Promise<{ data: Mesh[], total_count: number }> {
  try {
    const response = await authenticatedFetch(`${getKernelApiBaseUrl()}/api/v1/meshes`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch meshes:', error);
    throw error;
  }
}

export async function fetchKernelsSimple(): Promise<{ data: Kernel[], total_count: number }> {
  try {
    const response = await authenticatedFetch(`${getKernelApiBaseUrl()}/api/v1/kernels/list`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page: 1,
        page_size: 10,
        filter: []
      }),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch kernels:', error);
    // Return empty result if API is not available
    return { data: [], total_count: 0 };
  }
} 

interface MeshNode {
  id: string;
  node_type: {
    kernelinstance?: {
      kernel_id: string;
      version_id: string;
    };
  };
  is_root: boolean;
  config: {
    target_endpoint_url: string;
    timeout: number;
  };
  created_at: string;
  updated_at: string;
}

interface MeshEdge {
  id: string;
  source_node_id: string;
  target_node_id: string;
  edge_type: string;
  target_endpoint_url?: string;
  parameter_mappings: Array<{
    source_param: string | null;
    target_param: string;
    static_value: string | number | boolean | null;
  }>;
  created_at: string;
}

interface MeshGraphData {
  nodes: MeshNode[];
  edges: MeshEdge[];
  mesh_id: string;
  root_kernels: Record<string, unknown>;
}

export async function fetchMeshGraph(meshId: string): Promise<MeshGraphData> {
  try {
    console.log(`Fetching graph data from: ${getKernelApiBaseUrl()}/api/v1/meshes/${meshId}/graph`);
    const response = await authenticatedFetch(`${getKernelApiBaseUrl()}/api/v1/meshes/${meshId}/graph`);
    
    console.log('Graph API response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Graph API error response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    const data = await response.json();
    console.log('Graph API raw response:', data);
    return data;
  } catch (error) {
    console.error('Failed to fetch mesh graph:', error);
    throw error;
  }
}

export async function fetchKernelsByIds(kernelIds: string[]): Promise<Kernel[]> {
  try {
    console.log('fetchKernelsByIds called with:', kernelIds);
    
    if (kernelIds.length === 0) {
      console.log('No kernel IDs provided, returning empty array');
      return [];
    }

    const requestBody = {
      page: 1,
      page_size: 100,
      "filter[]": kernelIds
    };
    
    console.log('Making API request with body:', requestBody);
    
    const response = await authenticatedFetch(`${getKernelApiBaseUrl()}/api/v1/kernels/get`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });
    
    console.log('API response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    const data = await response.json();
    console.log('API response data:', data);
    return data.data as Kernel[] || [];
  } catch (error) {
    console.error('Failed to fetch kernels by IDs:', error);
    throw error;
  }
}

export async function deleteKernel(kernelId: string): Promise<void> {
  try {
    const response = await authenticatedFetch(`${getKernelApiBaseUrl()}/api/v1/kernels/delete/${kernelId}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error) {
    console.error('Failed to delete kernel:', error);
    throw error;
  }
}

export async function deleteMesh(meshId: string): Promise<void> {
  try {
    const response = await authenticatedFetch(`${getKernelApiBaseUrl()}/api/v1/meshes/${meshId}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
  } catch (error) {
    console.error('Failed to delete mesh:', error);
    throw error;
  }
} 
